import React from 'react';
import { get, isNil, map, mapKeys, filter } from 'lodash';
import classnames from 'classnames';
import { Checkbox, CircularProgress, Typography, Stack } from '@mui/material';
import { ReactComponent as RefreshIcon } from 'new/components/icons/refresh.svg';
import { ReactComponent as ApacheKafka } from 'new/components/icons/apache_kafka_symbol.svg';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import { Button } from 'new/elements/Button';
import { formatCode } from 'js/lib/FormatUtils';
import { TextField } from 'new/elements/TextField';
import TransformsUtils from 'js/lib/TransformsUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import KafkaTopicsListContainer from 'js/components/data_sources/add_ds_form/KafkaTopicsListContainer';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu/ResourcePopoverMenu';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import {
  FieldBuilderRedesigned,
  HeaderField,
} from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import DebouncedText from 'js/components/elements/editors/mui/DebouncedText';
import UIUtils from 'js/lib/UIUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';

import JSONUtils from 'js/lib/JSONUtils';
import { UiMode } from 'new/constants/uiMode';
import { withUiModeDefault } from 'new/utils/uiMode';

import styles from './KafkaSinkConfig.module.scss';
import { tryCatchAsync } from 'new/utils/TryCatch';

class KafkaSinkConfig extends React.Component {
  constructor(props) {
    super(props);

    this._handleNewTopicChecked = this._handleNewTopicChecked.bind(this);
    this._handleTopicNameChanged = this._handleTopicNameChanged.bind(this);
    this._handleSubscriptionChanged = this._handleSubscriptionChanged.bind(this);
    this._handleTopicChanged = this._handleTopicChanged.bind(this);
    this._handleRefreshPayload = this._handleRefreshPayload.bind(this);
    this._getTopicFieldName = this._getTopicFieldName.bind(this);
    this._showSubscription = this._showSubscription.bind(this);
    this._getSelectField = this._getSelectField.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._handleSinkConfigChanged = this._handleSinkConfigChanged.bind(this);
  }

  _getTopicFieldName() {
    const {
      paging: { sinkConfigById },
      selectedConnection,
    } = this.props;
    const fields = sinkConfigById[selectedConnection];
    const fieldsByName = mapKeys(fields, 'name');

    // Sink write-target key by connector shape:
    //   JMS                                 → 'target.name'
    //   Kafka / Confluent Kafka / Pub/Sub   → 'topic'
    // 'subscription' is an auxiliary sink field on Pub/Sub (auto-create on new topic),
    // not the write destination, so it must not preempt 'topic' here.
    if (fieldsByName['target.name']) return 'target.name';
    if (fieldsByName['topic']) return 'topic';

    return 'topic';
  }

  componentDidMount() {
    // Only fetch samples if we have toNested (indicating samples are loaded)
    if (this.props.toNested) {
      this._handleRefreshPayload();
    }
  }

  componentDidUpdate(prevProps) {
    // If toNested becomes available after mount, fetch samples
    if (!prevProps.toNested && this.props.toNested) {
      this._handleRefreshPayload();
    }
  }

  render() {
    const {
      paging: { metaInfoById, sinkConfigById },
      selectedConnection,
      topics,
      queues,
      selectedCredentialId,
      sinkConfig,
      dataSchema,
      previewPayload,
      previewPayloadError,
      selectedTopic,
      isNewTopicChecked,
      newTopicName,
      subscription,
      isLoadingPayload,
      dataSetId,
      addedDataSink,
      dataSet,
      sinkConfigFields,
    } = this.props;

    const meta = metaInfoById[selectedConnection];
    const connectionName = meta ? meta.displayName : 'Kafka';
    const fields = sinkConfigById[selectedConnection];
    const fieldsByName = mapKeys(fields, 'name');

    const showSubscription = this._showSubscription();
    const topicFieldName = this._getTopicFieldName();
    const topicsToUse = this._computeTopicsToUse();

    const editorContent = previewPayload
      ? formatCode(JSON.stringify(previewPayload), 'application/json')
      : '';

    const shouldIncludeMetadata =
      sinkConfig && !isNil(sinkConfig['include.metadata']) ? sinkConfig['include.metadata'] : false;

    const messageKeyOptions = map(dataSchema, (attr) => ({
      label: attr.__name,
      value: attr.__name,
    }));

    const messageKeySelection = getCurrentSelectedOption(
      messageKeyOptions,
      get(sinkConfig, 'message.key'),
    );

    const destName = addedDataSink?.name || sinkConfig?.name;
    const destDesc = addedDataSink?.description || sinkConfig?.description;

    const resourceConfig = this.getResourceConfig();

    // Build dynamic fields list from connector configuration
    const dynamicFields = [];

    // Add fields from sinkConfigFields that are not already handled by the UI
    const excludedFields = [
      'target.type',
      'topic',
      'target.name',
      'include.metadata',
      'message.key',
      'writer.type',
      'jms.vendor',
      // Rendered explicitly inside the create-new block (see inputTargetName
      // and the {isNewTopicChecked && ...} subscription input below) and
      // gated on isNewTopicChecked to match the schema's dependsOn rule.
      // The dynamic-fields loop doesn't honor dependsOn, so listing it here
      // prevents a duplicate unconditionally-visible input.
      'subscription',
    ];

    if (sinkConfigFields && sinkConfigFields.length > 0) {
      sinkConfigFields.forEach((field) => {
        if (!excludedFields.includes(field.name)) {
          dynamicFields.push({
            item: {
              display_name: field.display_name,
              description: field.description,
            },
            input:
              field.type === 'boolean' ? (
                <div className={styles.base_container}>
                  <div className={styles.row}>
                    <Checkbox
                      checked={!!get(sinkConfig, field.name, field.default || false)}
                      onChange={(_, isChecked) =>
                        this._handleSinkConfigChanged(field.name, isChecked)
                      }
                    />

                    <label className={styles.topic_label}>{field.display_name}</label>
                  </div>
                </div>
              ) : field.values ? (
                this._getSelectField(field)
              ) : (
                this._getTextField(field.name)
              ),
          });
        }
      });
    }

    // Build the main configuration fields in the correct order
    const inputTargetType = fieldsByName['target.type']
      ? {
          item: {
            display_name: 'Configure Type of Destination',
            description: fieldsByName['target.type'].description,
          },
          input: this._getSelectField(fieldsByName['target.type']),
        }
      : {};

    const topicField = fieldsByName[topicFieldName];
    const inputTargetName = topicField
      ? {
          item: {
            display_name: fieldsByName['target.type']
              ? `Select ${connectionName} topic or queue to write to`
              : `Select ${connectionName} topic to write to`,
            description: topicField.description,
          },
          input: (
            <div
              className={classnames(
                styles.base_style_container,
                topicsToUse?.length ? styles.border_none : undefined,
              )}
            >
              <KafkaTopicsListContainer
                topicFieldName={topicFieldName}
                topics={topicsToUse}
                // Reads form-state `selectedTopic` directly (not sinkConfig).
                // The reconciler updates sinkConfig asynchronously on the next
                // commit, so reading sinkConfig here would expose the picker
                // to a transient stale value (e.g. the typed new-topic name)
                // while in pick-existing mode — which SearchableList would
                // then treat as a customItem and feedback-loop into infinite
                // setState. Form-state selectedTopic is null whenever
                // create-new mode is active (see _handleNewTopicChecked).
                selectedTopic={selectedTopic}
                selectedCredentialId={selectedCredentialId}
                sourceType={get(sinkConfig, 'target.type', 'topic')}
                onTopicChanged={this._handleTopicChanged}
              />
            </div>
          ),
        }
      : {};

    const inputDataFormat = fieldsByName['writer.type']
      ? {
          item: {
            display_name: 'Data format',
            description: fieldsByName['writer.type'].description,
          },
          input: this._getSelectField(fieldsByName['writer.type']),
        }
      : {};

    const inputJmsVendor = fieldsByName['jms.vendor']
      ? {
          item: {
            display_name: 'Select JMS Vendor',
            description: fieldsByName['jms.vendor'].description,
          },
          input: this._getSelectField(fieldsByName['jms.vendor']),
        }
      : {};

    const listone = [inputJmsVendor, inputTargetType].filter((i) => i?.item);

    const listtwo = [inputTargetName].filter((i) => i?.item);

    const listthree = [inputDataFormat].filter((i) => i?.item);

    return (
      <div className={styles.container}>
        <HeaderMetaConfiguration
          nexsetId={this.props.dataSetId}
          isDestination
          name={destName}
          description={destDesc}
          connection={selectedConnection}
          onChange={({ name, value }) => this.updateSinkConfig(name, value)}
          nexsetChipChildren={
            <ResourcePopoverMenu
              isDestination
              nexsetId={this.props.dataSetId}
              resourceConfig={resourceConfig}
              connectorType={selectedConnection}
              jsonConfigOnSave={this.onSaveAdvancedMode}
              jsonConfigOnClose={() => {
                if (this.props.goBack) {
                  this.props.goBack();
                }
              }}
              onSwitchUiMode={this.onSwitchUiMode}
              disabledActions={['view-json-config']}
            />
          }
          sourceOrDestinationChipChildren={
            <ResourcePopoverMenu
              isDestination
              resourceConfig={resourceConfig}
              connectorType={selectedConnection}
              jsonConfigOnSave={this.onSaveAdvancedMode}
              jsonConfigOnClose={() => {
                if (this.props.goBack) {
                  this.props.goBack();
                }
              }}
              onSwitchUiMode={this.onSwitchUiMode}
              disabledActions={['view-nexset-details']}
            />
          }
        />

        <Stack padding={2} className={styles.inner_container}>
          <div className={classnames(styles.side, styles.padding)}>
            {inputJmsVendor.item && (
              <div className={styles.base_container}>
                <label className={styles.topic_label}>{inputJmsVendor.item.display_name}</label>
                {inputJmsVendor.input}
              </div>
            )}

            {inputTargetType.item && (
              <div className={styles.base_container}>
                <label className={styles.topic_label}>{inputTargetType.item.display_name}</label>
                {inputTargetType.input}
              </div>
            )}

            {inputTargetName.item && (
              <div className={styles.base_container}>
                <label className={styles.topic_label}>{inputTargetName.item.display_name}</label>
                {inputTargetName.input}
              </div>
            )}
            <div className={styles.base_container}>
              <label className={styles.topic_label}>Or Create Topic/Queue to Write To</label>
              <div className={styles.row}>
                <Checkbox
                  id="create_new_topic"
                  checked={isNewTopicChecked || false}
                  onChange={this._handleNewTopicChecked}
                />

                <label htmlFor="create_new_topic">Create a New Topic</label>
              </div>
            </div>

            {isNewTopicChecked && (
              <div className={styles.base_container} style={{ marginBottom: 'var(--spacing-xl)' }}>
                <label className={styles.topic_label}>Topic Name</label>
                <div>
                  <TextField
                    value={newTopicName || ''}
                    placeholder="Enter a name"
                    onChange={this._handleTopicNameChanged}
                  />
                </div>

                {showSubscription ? (
                  <div className={styles.base_container}>
                    <label className={styles.topic_label}>Subscription</label>
                    <div>
                      <TextField
                        value={subscription || ''}
                        placeholder="Enter subscription name"
                        onChange={this._handleSubscriptionChanged}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Include Metadata checkbox */}
            <div className={styles.base_container}>
              <label className={styles.topic_label}>Include Nexla Metadata in Message?</label>
              <div className={styles.row}>
                <Checkbox
                  checked={shouldIncludeMetadata}
                  onChange={(_, isChecked) => this.updateSinkConfig('include.metadata', isChecked)}
                />
              </div>
            </div>

            {/* Message Key selection */}
            <div className={styles.base_container}>
              <label className={styles.topic_label}>
                Select attribute to be used as message key
              </label>

              <SortedSelect
                variant="default"
                isClearable
                isSearchable
                backspaceRemovesValue
                styles={baseSelectStyles}
                value={messageKeySelection}
                options={messageKeyOptions}
                components={{ Input: SelectInput, Option: SelectOption }}
                onChange={(newValue) => {
                  let value = getNewSelectionValue(newValue, true, '');
                  this.updateSinkConfig('message.key', value);
                }}
              />
            </div>

            {/* Dynamic fields from connector configuration */}
            {dynamicFields.map((field, index) => (
              <div key={field.item.display_name} className={styles.base_container}>
                <label className={styles.topic_label}>{field.item.display_name}</label>
                {field.input}
              </div>
            ))}
          </div>

          <div
            className={classnames(
              styles.side,
              styles.padding,
              styles.base_style_container,
              styles.monaco_view,
            )}
          >
            <div className={classnames(styles.samples_kafka_title, styles.padding)}>
              <span className={styles.title}>
                Samples Payload to {connectionName}{' '}
                {!!previewPayloadError && ` (Failure: ${previewPayloadError})`}
              </span>

              <Button
                variant="outlined"
                color="info"
                className={styles.refresh_payload}
                onClick={this._handleRefreshPayload}
              >
                {isLoadingPayload ? (
                  <CircularProgress size={20} style={{ color: 'inherit' }} />
                ) : (
                  <>
                    <RefreshIcon />
                    Refresh
                  </>
                )}
              </Button>
            </div>

            <div className={styles.monacoEditorContainer}>
              <LoadableMonacoEditor
                language="json"
                theme="dark"
                width="100%"
                height="100%"
                value={editorContent}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                }}
              />
            </div>
          </div>
        </Stack>
      </div>
    );
  }

  onSaveAdvancedMode = async (sinkConfigString) => {
    const { onSaveAdvancedMode, nextFormStage, onError = () => {} } = this.props;

    if (!JSONUtils.isJSON(sinkConfigString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const { name, description, sink_config } = JSON.parse(sinkConfigString);
    const data = {
      ...sink_config,
      name,
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sinkConfig, updateFormData, formType } = this.props;
    if (typeof updateFormData !== 'function') return;

    const sinkConfigWithViewMode = { ...sinkConfig, 'ui.mode': UiMode.VIEW };
    const formDataUpdate = { sinkConfig: sinkConfigWithViewMode };

    updateFormData(formType, formDataUpdate);
  };

  getResourceConfig = () => {
    const { sink, sinkConfig } = this.props;
    const clonedSinkConfig = structuredClone(sinkConfig);

    const name = sink?.name || clonedSinkConfig?.name || '';
    const description = sink?.description || clonedSinkConfig?.description || '';

    delete clonedSinkConfig?.name;
    delete clonedSinkConfig?.description;

    return {
      name,
      description,
      sink_config: clonedSinkConfig,
    };
  };

  updateSinkConfig = (name, value) => {
    this.props.updateFormData(this.props.formType, {
      sinkConfig: {
        ...this.props.sinkConfig,
        [name]: value,
      },
    });
  };

  // Pick-existing and create-new are mutually exclusive modes. Each mode flip
  // clears the other mode's form state, so the visible UI always matches what
  // will actually be saved — no hidden typed name preserved behind a pick, no
  // stale pick preserved behind a typed name. The parent reconciler then
  // derives sinkConfig from whichever mode is active.
  _handleNewTopicChecked(_, isNewTopicChecked) {
    const { formType, updateFormData } = this.props;

    updateFormData(formType, {
      isNewTopicChecked,
      selectedTopic: null,
      newTopicName: '',
      subscription: '',
    });
  }

  _handleTopicNameChanged(event) {
    const { formType, updateFormData } = this.props;
    const newTopicName = event.target.value;

    // Form-level field only. The parent reconciler syncs this into
    // sinkConfig[topicFieldName] because isNewTopicChecked is true.
    updateFormData(formType, { newTopicName });
  }

  _handleSubscriptionChanged(event) {
    const { formType, updateFormData } = this.props;

    updateFormData(formType, { subscription: event.target.value });
  }

  _handleTopicChanged(selectedTopic) {
    const { formType, updateFormData } = this.props;

    // SearchableList re-emits onChange(value) whenever its selectedItem prop
    // changes externally — including onChange(null) on a clear, and
    // onChange(<customItem>) when the prop is a value not in the list. Both
    // can fire as a feedback loop when this component updates a prop that
    // SearchableList consumes. Real user picks always carry a non-empty
    // existing topic name, so bailing on falsy is a safe guard.
    if (!selectedTopic) return;

    // Picking an existing topic exits create-new mode and clears any stale
    // typed-name / subscription so the UI shows only what will actually be
    // saved. The reconciler then sets sinkConfig[topicFieldName] = selectedTopic.
    updateFormData(formType, {
      selectedTopic,
      isNewTopicChecked: false,
      newTopicName: '',
      subscription: '',
    });
  }

  _handleRefreshPayload() {
    const { dataSetId, toNested, fetchTransformedPayloadFromEditorForRest } = this.props;
    const txObject = TransformsUtils.getTransformSpecForFlatToNested();

    fetchTransformedPayloadFromEditorForRest(txObject, toNested, dataSetId);
  }

  // _handleMetaChange = ({ name, value }) => {
  //   this.updateSinkConfig(name, value);
  // };

  _showSubscription() {
    const { sinkConfigFields } = this.props;
    let subscriptionField = sinkConfigFields.find((field) => field.name === 'subscription');
    return !!subscriptionField;
  }

  _getSelectField(field, sort = false) {
    const { name } = field;
    const { sinkConfig } = this.props;
    const listOfClass = classnames(styles.base_container);

    return (
      <div className={listOfClass}>
        <FieldBuilderRedesigned
          id={name}
          field={field}
          isRequired={field.required}
          showRequiredIndicator
          labeled={false}
          currentFieldValue={get(sinkConfig, name)}
          isClearable={!sort}
          backspaceRemovesValue={!sort}
          isSearchable={sort}
          onChange={(name, currValue) => {
            this._handleSinkConfigChanged(name, currValue);
          }}
        />
      </div>
    );
  }

  _getTextField(name) {
    const { sinkConfig } = this.props;
    const value = get(sinkConfig, name, '');

    return (
      <div className={classnames(styles.base_container)}>
        <DebouncedText
          id={name}
          value={value}
          rootStyle={{ height: 38, borderRadius: 2 }}
          style={{ padding: '2px 8px' }}
          onChange={(newValue) => this._handleSinkConfigChanged(name, newValue)}
        />
      </div>
    );
  }

  _computeTopicsToUse() {
    const {
      paging: { sinkConfigById },
      selectedConnection,
      topics,
      queues,
      sinkConfig,
    } = this.props;

    const fields = sinkConfigById[selectedConnection];
    const fieldsByName = mapKeys(fields, 'name');
    const hasTargetTypeField = fieldsByName['target.type'];
    const currentTargetType = get(sinkConfig, 'target.type', 'topic');
    const isQueueType = hasTargetTypeField && currentTargetType === 'queue';

    return isQueueType ? queues || [] : topics || [];
  }

  _handleSinkConfigChanged(name, value) {
    const {
      paging: { sinkConfigById },
      formType,
      sinkConfig,
      selectedConnection,
      updateFormData,
    } = this.props;
    const fields = sinkConfigById[selectedConnection];

    let updatedConfig = {
      ...sinkConfig,
      [name]: value,
    };

    // If target.type changed, clear the selected topic/queue since it's no longer valid
    if (name === 'target.type') {
      const topicFieldName = this._getTopicFieldName();
      updatedConfig[topicFieldName] = null;
    }

    const newSinkConfig = UIUtils.configWithMissingDefaults(updatedConfig, fields);
    const sinkConfigWithMode = withUiModeDefault(newSinkConfig);
    const sinkConfigFormDataUpdate = { sinkConfig: sinkConfigWithMode };

    // Update sinkConfig
    updateFormData(formType, sinkConfigFormDataUpdate);

    // Also update form-level properties for validation
    const topicFieldName = this._getTopicFieldName();
    if (name === topicFieldName) {
      // Update form-level selectedTopic for validation
      const selectedTopicFormDataUpdate = { selectedTopic: value };
      updateFormData(formType, selectedTopicFormDataUpdate);
    }
  }

  validateRequiredFields = (config) => {
    const { selectedConnection, sinkConfigFields } = this.props;
    const fields = sinkConfigFields[selectedConnection];
    const missingFields = getEmptyRequiredFields(fields, config);
    return !missingFields.length;
  };
}

export default KafkaSinkConfig;
